import React, { useState, BaseSyntheticEvent } from 'react'
import { PhotoViewer } from '@capacitor-community/photoviewer'
import { IonSpinner, IonItem, IonToggle, IonGrid, IonRow, IonCol, IonIcon } from '@ionic/react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import LabelComponent from '../../../../components/label/Label.component'
import { getPulsaraDetailsAttachments } from '../../selectors'
import { fetchPulsaraAttachmentImage } from '../../store/pulsara.actions'
import { IPulsaraAttachmentItem, IPulsaraAttachmentImage } from '../../store/pulsara.types'
import { chevronDown, chevronUp } from 'ionicons/icons'
import { convertImageSizeToKB } from '../../utils/pulsaraAttachment.util'
import { IPulsaraAttachmentColumn } from './PulsaraAttachments.component'

interface PulsaraAttachmentRowProps {
  row: IPulsaraAttachmentItem
  isSelected: boolean
  columns: IPulsaraAttachmentColumn[]
  onToggleItem: (checked: boolean, item: IPulsaraAttachmentItem) => void
}

const PulsaraAttachmentRow: React.FC<PulsaraAttachmentRowProps> = ({ row, isSelected, columns, onToggleItem }) => {
  const { t } = useTranslation()
  const attachments = useSelector(getPulsaraDetailsAttachments)
  const dispatch = useDispatch()
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpand = () => {
    if (!isExpanded) {
      const attachment = attachments?.find((a: IPulsaraAttachmentImage) => a.id === row.id) ?? null
      if (!attachment) {
        dispatch(fetchPulsaraAttachmentImage({ attachmentId: row.id, pulsaraId: row.pulsaraId }))
      }
    }
    setIsExpanded((prev) => !prev)
  }

  const showPhotoViewer = (photoViewerProps: { src: string; alt: string }) => {
    const { src, alt } = photoViewerProps

    PhotoViewer.show({
      images: [
        {
          url: src || '',
          title: alt,
        },
      ],
      options: {
        share: false,
      },
    })
  }

  const renderExpandedContent = () => {
    const attachment = attachments?.find((a: IPulsaraAttachmentImage) => a.id === row.id) ?? null

    if (!attachment) return <IonSpinner />

    const sizeLabel = convertImageSizeToKB(attachment.size)

    return (
      <div className='pulsara-attachment-preview'>
        <div className='pulsara-attachment-strip'>
          {attachment.blobData ? (
            <div className='pulsara-attachment-strip-image'>
              <img
                src={`data:${attachment.mime || 'image/png'};base64,${attachment.blobData}`}
                alt={attachment.name}
                onClick={() =>
                  showPhotoViewer({
                    src: `data:${attachment.mime || 'image/png'};base64,${attachment.blobData}`,
                    alt: attachment.name,
                  })
                }
              />
            </div>
          ) : (
            <>
              {attachment.isUnavailable ? (
                <div className='pulsara-attachment-strip-no-image'>{t('pulsara.PreviewUnavailable')}</div>
              ) : (
                <IonSpinner />
              )}
            </>
          )}
        </div>
        {sizeLabel ? <div className='pulsara-attachment-preview-size'>{sizeLabel}</div> : null}
      </div>
    )
  }
  return (
    <>
      <IonItem key={row.id} className='pulsara-attachment-main'>
        <IonToggle
          slot='start'
          checked={isSelected}
          onClick={(e: BaseSyntheticEvent) => onToggleItem(e.target.checked, row)}
        />
        <IonGrid className='pulsara-attachment-grid'>
          <IonRow className='pulsara-attachment-row'>
            {columns.map((column) => {
              const value = row[column.key]
              const displayValue = value != null && value !== '' ? String(value) : ''
              return (
                <IonCol
                  key={column.key}
                  className={`pulsara-attachment-column pulsara-attachment-column-${
                    column.key
                  } pulsara-attachment-data-point ${column.isDescription ? 'pulsara-attachment-description' : ''}`}>
                  <LabelComponent isPHI={false} label={displayValue} size={3} />
                </IonCol>
              )
            })}

            <IonCol className='pulsara-attachment-more' onClick={toggleExpand}>
              <IonIcon
                color='medium'
                className='pulsara-attachment-more-icon'
                icon={isExpanded ? chevronUp : chevronDown}
              />
            </IonCol>
          </IonRow>
        </IonGrid>
      </IonItem>
      {isExpanded && <div className='pulsara-attachment-expanded'>{renderExpandedContent()}</div>}
    </>
  )
}

export default PulsaraAttachmentRow
